// Sleep, counted properly. Logged naps can overlap (both phones tapped Nap,
// or a nap "logged later" on top of one already there) and can be left open
// (nobody tapped Woke up), so every sum goes through merged intervals:
// overlaps count once, and an open nap counts for at most 12 hours.

import { DAY, HOUR, MINUTE, atClock, dayStart, inClockWindow } from './time.ts';
import type { EventType, LogEvent, Millis } from './types.ts';

/** An open nap stops counting after this long — someone forgot to tap Woke up. */
export const OPEN_SLEEP_CAP = 12 * HOUR;

export type Interval = [Millis, Millis];

/** Merged sleep intervals within [from, to]. Open naps run to `now` (at most 12 hours). */
export function sleepIntervals(events: LogEvent[], from: Millis, to: Millis, now: Millis = to): Interval[] {
  const raw: Interval[] = [];
  for (const e of events) {
    if (e.type !== 'sleep' || e.at > now) continue;
    const end = e.end ?? Math.min(now, e.at + OPEN_SLEEP_CAP);
    const s = Math.max(e.at, from);
    const t = Math.min(end, to);
    if (t > s) raw.push([s, t]);
  }
  raw.sort((a, b) => a[0] - b[0]);
  const out: Interval[] = [];
  for (const iv of raw) {
    const last = out[out.length - 1];
    if (last && iv[0] <= last[1]) last[1] = Math.max(last[1], iv[1]);
    else out.push([iv[0], iv[1]]);
  }
  return out;
}

const total = (ivs: Interval[]) => ivs.reduce((n, [s, t]) => n + (t - s), 0);

/** Total sleep in [from, to], each minute counted once. */
export function sleepWithin(events: LogEvent[], from: Millis, to: Millis): Millis {
  return total(sleepIntervals(events, from, to));
}

/** When the household's settings aren't to hand. */
export const DEFAULT_NIGHT: NightWindow = { start: '21:30', end: '06:30' };

export interface NightWindow {
  /** "21:30" */
  start: string;
  /** "06:30" */
  end: string;
}

export interface SleepDay {
  /** Local midnight. */
  date: Millis;
  /** All sleep in the 24 hours. */
  totalMs: Millis;
  /** Naps: sleep that starts between the end of last night and the start of tonight. */
  naps: number;
  napMs: Millis;
  /** Logged well enough to judge: at least three sleeps touch the day. */
  wellLogged: boolean;
}

/** One calendar day's sleep. */
export function sleepDay(events: LogEvent[], day: Millis, now: Millis, night: NightWindow): SleepDay {
  const from = dayStart(day);
  const to = Math.min(dayStart(from, 1), now);
  const ivs = sleepIntervals(events, from, to, now);
  const dayFrom = atClock(from, night.end);
  const dayTo = atClock(from, night.start);
  const naps = ivs.filter(([s]) => s >= dayFrom && s < dayTo);
  return { date: from, totalMs: total(ivs), naps: naps.length, napMs: total(naps), wellLogged: ivs.length >= 3 };
}

export interface SleepNight {
  /** The morning it ended (local midnight of that day). */
  date: Millis;
  /** Sleep between bedtime and getting up. */
  sleepMs: Millis;
  /** Longest unbroken stretch. */
  longestMs: Millis;
  /** Times he was up in the night: breaks between sleeps, plus any wee or poo logged in the night. */
  wakes: number;
  /** Any sleep logged that night at all. */
  logged: boolean;
}

/** The night that ended on the morning of `day` (e.g. 21:30 → 06:30). */
export function sleepNight(events: LogEvent[], day: Millis, now: Millis, night: NightWindow): SleepNight {
  const morning = dayStart(day);
  const from = atClock(morning - DAY, night.start);
  const to = Math.min(atClock(morning, night.end), now);
  // Sleep that started a little before bedtime still counts for the night.
  const ivs = sleepIntervals(events, from - 2 * HOUR, to, now).filter(([, t]) => t > from);
  const clipped = ivs.map(([s, t]) => [Math.max(s, from), t] as Interval).filter(([s, t]) => t > s);
  const breaks = Math.max(0, clipped.length - 1);
  const toilets = events.filter((e) => (e.type === 'wee' || e.type === 'poo') && e.at > from && e.at < to).length;
  return {
    date: morning,
    sleepMs: total(clipped),
    longestMs: clipped.reduce((m, [s, t]) => Math.max(m, t - s), 0),
    // A night wee usually also shows as a break between two sleeps — count the bigger of the two.
    wakes: Math.max(breaks, toilets),
    logged: clipped.length > 0,
  };
}

/** Average sleep per day over the well-logged days of the last `days` whole days (undefined if none). */
export function averageDailySleep(events: LogEvent[], now: Millis, days: number, night: NightWindow): { hours: number; days: number } | undefined {
  const list = Array.from({ length: days }, (_, i) => sleepDay(events, dayStart(now, -(i + 1)), now, night)).filter((d) => d.wellLogged);
  if (!list.length) return undefined;
  return { hours: list.reduce((n, d) => n + d.totalMs, 0) / list.length / HOUR, days: list.length };
}

/** Minutes awake between naps (end of one sleep to the start of the next) over [from, to], daytime only. */
export function awakeStretches(events: LogEvent[], from: Millis, to: Millis, night: NightWindow): number[] {
  const ivs = sleepIntervals(events, from, to);
  const out: number[] = [];
  for (let i = 1; i < ivs.length; i++) {
    const woke = ivs[i - 1][1];
    const gap = (ivs[i][0] - woke) / MINUTE;
    const day = dayStart(woke);
    if (woke >= atClock(day, night.end) && woke < atClock(day, night.start) && gap > 5 && gap < 4 * 60) out.push(gap);
  }
  return out;
}

/** Things he can only do awake. */
const AWAKE: ReadonlySet<EventType> = new Set<EventType>(['wee', 'poo', 'meal', 'training', 'play', 'walk', 'social', 'behaviour']);

/** He can't do `e` asleep (a wee, a meal, training…). */
export const awakeOnly = (e: LogEvent): boolean => AWAKE.has(e.type);

/** Overnight, a wee inside the night's sleep is a normal quick break — only daytime naps are checked. */
const daytime = (t: Millis, night: NightWindow) => !inClockWindow(t, night.start, night.end);

/**
 * A daytime nap with something logged in the middle of it that he must have
 * been awake for — a wee at 17:00 inside a nap that "ended" at 17:22 means he
 * woke by 17:00 and Woke up was tapped late. The first such thing, so the
 * nap can end there.
 */
export function napClash(nap: LogEvent, events: LogEvent[], now: Millis, night: NightWindow): LogEvent | undefined {
  if (nap.type !== 'sleep' || !daytime(nap.at, night)) return undefined;
  const end = nap.end ?? Math.min(now, nap.at + OPEN_SLEEP_CAP);
  // A couple of minutes' grace either side: logged as he went down, or just as he woke.
  return events
    .filter((e) => awakeOnly(e) && e.at > nap.at + 3 * MINUTE && e.at < end - 3 * MINUTE && daytime(e.at, night))
    .sort((a, b) => a.at - b.at)[0];
}

/**
 * Past bedtime but not yet down for the night: still up after a late dinner
 * or game. Then the next thing is a last wee and bed — not a "night wee"
 * (that's for when he's slept and woken). Only in the first few hours of the
 * night: at 5am an unlogged bedtime is more likely than a puppy still up.
 */
export function bedtimePending(events: LogEvent[], now: Millis, night: NightWindow): boolean {
  if (!inClockWindow(now, night.start, night.end)) return false;
  const bed = atClock(now, night.start) <= now ? atClock(now, night.start) : atClock(dayStart(now, -1), night.start);
  if (now - bed > 3 * HOUR) return false;
  // Down for the night: a sleep from around bedtime that has lasted (or is lasting) a while — not a short evening nap.
  return !events.some((e) => e.type === 'sleep' && e.at >= bed - 30 * MINUTE && e.at <= now && ((e.end ?? now) - e.at >= 90 * MINUTE || e.end === undefined));
}

/**
 * The open daytime naps that logging `e` ends: he can't wee, eat or train
 * asleep, so a nap still running when one of those is logged ended then.
 * Overnight a wee is a quick break in the night's sleep, so that's left be.
 * Shared by the app and the home-screen widgets.
 */
export function napsEndedBy(e: LogEvent, events: LogEvent[], night: NightWindow): LogEvent<'sleep'>[] {
  if (!awakeOnly(e) || inClockWindow(e.at, night.start, night.end)) return [];
  return events.filter(
    (n): n is LogEvent<'sleep'> => n.type === 'sleep' && n.end === undefined && n.at < e.at - 3 * MINUTE && e.at - n.at < OPEN_SLEEP_CAP && !inClockWindow(n.at, night.start, night.end),
  );
}
